import {
  collection,
  doc,
  addDoc,
  getDocs,
  query,
  where,
  deleteDoc,
  Timestamp,
  getDoc,
  onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { createNotification } from './notificationsService';

// ============================================
// COMPARTILHAMENTO DE ACESSO AO INVENTÁRIO COMPLETO
// ============================================

export interface InventoryAccess {
  id: string;
  ownerId: string;
  ownerName: string;
  ownerEmail: string;
  grantedToUserId: string;
  grantedToUserName: string;
  grantedToUserEmail: string;
  grantedAt: Date;
  status: 'pending' | 'accepted' | 'rejected';
}

const INVENTORY_ACCESS_COLLECTION = 'inventoryAccess';

/**
 * Concede acesso ao inventário completo para outro usuário
 */
export const grantInventoryAccess = async (
  ownerId: string,
  ownerName: string,
  ownerEmail: string,
  targetUserId: string,
  targetUserName: string,
  targetUserEmail: string
): Promise<string> => {
  try {
    // Verificar se já existe acesso concedido
    const existingQuery = query(
      collection(db, INVENTORY_ACCESS_COLLECTION),
      where('ownerId', '==', ownerId),
      where('grantedToUserId', '==', targetUserId)
    );
    const existingSnap = await getDocs(existingQuery);

    if (!existingSnap.empty) {
      throw new Error('Acesso já foi concedido a este usuário');
    }

    // Criar registro de acesso
    const accessData = {
      ownerId,
      ownerName,
      ownerEmail,
      grantedToUserId: targetUserId,
      grantedToUserName: targetUserName,
      grantedToUserEmail: targetUserEmail,
      grantedAt: Timestamp.now(),
      status: 'accepted', // Acesso direto, sem necessidade de aceitar
    };

    const docRef = await addDoc(collection(db, INVENTORY_ACCESS_COLLECTION), accessData);

    // Notificar usuário que recebeu acesso
    await createNotification({
      userId: targetUserId,
      type: 'general',
      title: 'Acesso ao acervo concedido',
      message: `${ownerName} compartilhou o acervo completo com você. Agora você pode visualizar todos os itens.`,
      senderId: ownerId,
      senderName: ownerName,
      metadata: {
        type: 'inventory_access_granted',
        ownerId,
      },
    });

    return docRef.id;
  } catch (error) {
    console.error('Erro ao conceder acesso ao inventário:', error);
    throw error;
  }
};

/**
 * Revoga acesso ao inventário
 */
export const revokeInventoryAccess = async (accessId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, INVENTORY_ACCESS_COLLECTION, accessId));
  } catch (error) {
    console.error('Erro ao revogar acesso:', error);
    throw error;
  }
};

/**
 * Busca usuários que têm acesso ao MEU inventário
 */
export const getUsersWithAccessToMyInventory = async (ownerId: string): Promise<InventoryAccess[]> => {
  try {
    const q = query(
      collection(db, INVENTORY_ACCESS_COLLECTION),
      where('ownerId', '==', ownerId),
      where('status', '==', 'accepted')
    );
    const snapshot = await getDocs(q);

    const accesses: InventoryAccess[] = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      accesses.push({
        id: docSnap.id,
        ownerId: data.ownerId,
        ownerName: data.ownerName,
        ownerEmail: data.ownerEmail,
        grantedToUserId: data.grantedToUserId,
        grantedToUserName: data.grantedToUserName,
        grantedToUserEmail: data.grantedToUserEmail,
        grantedAt: data.grantedAt?.toDate() || new Date(),
        status: data.status,
      });
    });

    return accesses;
  } catch (error) {
    console.error('Erro ao buscar acessos concedidos:', error);
    return [];
  }
};

/**
 * Busca inventários aos quais EU tenho acesso (de outros usuários)
 */
export const getInventoriesIHaveAccessTo = async (userId: string): Promise<InventoryAccess[]> => {
  try {
    const q = query(
      collection(db, INVENTORY_ACCESS_COLLECTION),
      where('grantedToUserId', '==', userId),
      where('status', '==', 'accepted')
    );
    const snapshot = await getDocs(q);

    const accesses: InventoryAccess[] = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      accesses.push({
        id: docSnap.id,
        ownerId: data.ownerId,
        ownerName: data.ownerName,
        ownerEmail: data.ownerEmail,
        grantedToUserId: data.grantedToUserId,
        grantedToUserName: data.grantedToUserName,
        grantedToUserEmail: data.grantedToUserEmail,
        grantedAt: data.grantedAt?.toDate() || new Date(),
        status: data.status,
      });
    });

    return accesses;
  } catch (error) {
    console.error('Erro ao buscar inventários com acesso:', error);
    return [];
  }
};

/**
 * Busca itens do inventário de outro usuário (se tiver acesso)
 */
export const getSharedInventoryItems = async (ownerId: string, viewerId: string): Promise<any[]> => {
  try {
    // Verificar se o viewer tem acesso
    const accessQuery = query(
      collection(db, INVENTORY_ACCESS_COLLECTION),
      where('ownerId', '==', ownerId),
      where('grantedToUserId', '==', viewerId),
      where('status', '==', 'accepted')
    );
    const accessSnap = await getDocs(accessQuery);

    if (accessSnap.empty) {
      throw new Error('Você não tem acesso a este acervo');
    }

    // Buscar itens do inventário do owner
    const itemsQuery = query(
      collection(db, 'inventoryItems'),
      where('userId', '==', ownerId)
    );
    const itemsSnap = await getDocs(itemsQuery);

    const items: any[] = [];
    itemsSnap.forEach((docSnap) => {
      const data = docSnap.data();
      items.push({
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      });
    });

    return items;
  } catch (error) {
    console.error('Erro ao buscar itens do inventário compartilhado:', error);
    throw error;
  }
};

/**
 * Listener em tempo real para inventários compartilhados
 */
export const subscribeToSharedInventories = (
  userId: string,
  callback: (accesses: InventoryAccess[]) => void
): Unsubscribe => {
  const q = query(
    collection(db, INVENTORY_ACCESS_COLLECTION),
    where('grantedToUserId', '==', userId),
    where('status', '==', 'accepted')
  );

  return onSnapshot(q, (snapshot) => {
    const accesses: InventoryAccess[] = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      accesses.push({
        id: docSnap.id,
        ownerId: data.ownerId,
        ownerName: data.ownerName,
        ownerEmail: data.ownerEmail,
        grantedToUserId: data.grantedToUserId,
        grantedToUserName: data.grantedToUserName,
        grantedToUserEmail: data.grantedToUserEmail,
        grantedAt: data.grantedAt?.toDate() || new Date(),
        status: data.status,
      });
    });
    callback(accesses);
  });
};

// ============================================
// COMPARTILHAMENTO DE ITENS INDIVIDUAIS (legado)
// ============================================

export interface SharedInventoryItem {
  id: string;
  originalItemId: string;
  originalOwnerId: string;
  sharedWithUserId: string;
  itemName: string;
  description?: string;
  photoUrl?: string;
  photoUrl2?: string;
  quantity?: number;
  unitValue?: number;
  brand?: string;
  store?: string;
  category?: string;
  measurements?: string;
  observations?: string;
  sharedAt: Date;
  sharedByName: string;
}

const SHARED_INVENTORY_COLLECTION = 'sharedInventory';

/**
 * Busca userId pelo email
 */
export const findUserByEmail = async (email: string): Promise<{ userId: string; displayName: string } | null> => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email.toLowerCase()));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    const userDoc = snapshot.docs[0];
    const userData = userDoc.data();
    return {
      userId: userDoc.id,
      displayName: userData.displayName || userData.name || email.split('@')[0],
    };
  } catch (error) {
    console.error('Erro ao buscar usuário por email:', error);
    return null;
  }
};

/**
 * Compartilha um item de inventário com outro usuário
 */
export const shareInventoryItem = async (
  itemId: string,
  ownerId: string,
  ownerName: string,
  targetUserId: string,
  _targetUserName: string
): Promise<boolean> => {
  try {
    // Buscar dados do item original
    const itemRef = doc(db, 'inventoryItems', itemId);
    const itemSnap = await getDoc(itemRef);

    if (!itemSnap.exists()) {
      throw new Error('Item não encontrado');
    }

    const itemData = itemSnap.data();

    // Verificar se já foi compartilhado com este usuário
    const existingQuery = query(
      collection(db, SHARED_INVENTORY_COLLECTION),
      where('originalItemId', '==', itemId),
      where('sharedWithUserId', '==', targetUserId)
    );
    const existingSnap = await getDocs(existingQuery);

    if (!existingSnap.empty) {
      throw new Error('Item já foi compartilhado com este usuário');
    }

    // Criar registro de compartilhamento
    const shareData = {
      originalItemId: itemId,
      originalOwnerId: ownerId,
      sharedWithUserId: targetUserId,
      itemName: itemData.itemName,
      description: itemData.description || null,
      photoUrl: itemData.photoUrl || null,
      photoUrl2: itemData.photoUrl2 || null,
      quantity: itemData.quantity || 1,
      unitValue: itemData.unitValue || null,
      brand: itemData.brand || null,
      store: itemData.store || null,
      category: itemData.category || null,
      measurements: itemData.measurements || null,
      observations: itemData.observations || null,
      sharedAt: Timestamp.now(),
      sharedByName: ownerName,
    };

    await addDoc(collection(db, SHARED_INVENTORY_COLLECTION), shareData);

    // Enviar notificação para o usuário
    await createNotification({
      userId: targetUserId,
      type: 'general',
      title: 'Item de acervo compartilhado',
      message: `${ownerName} compartilhou "${itemData.itemName}" com você.`,
      senderId: ownerId,
      senderName: ownerName,
      metadata: {
        itemId,
        itemName: itemData.itemName,
        type: 'inventory_share',
      },
    });

    return true;
  } catch (error) {
    console.error('Erro ao compartilhar item:', error);
    throw error;
  }
};

/**
 * Busca itens compartilhados COM o usuário (itens que outros compartilharam com ele)
 */
export const getItemsSharedWithMe = async (userId: string): Promise<SharedInventoryItem[]> => {
  try {
    const q = query(
      collection(db, SHARED_INVENTORY_COLLECTION),
      where('sharedWithUserId', '==', userId)
    );
    const snapshot = await getDocs(q);

    const items: SharedInventoryItem[] = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      items.push({
        id: docSnap.id,
        originalItemId: data.originalItemId,
        originalOwnerId: data.originalOwnerId,
        sharedWithUserId: data.sharedWithUserId,
        itemName: data.itemName,
        description: data.description,
        photoUrl: data.photoUrl,
        photoUrl2: data.photoUrl2,
        quantity: data.quantity,
        unitValue: data.unitValue,
        brand: data.brand,
        store: data.store,
        category: data.category,
        measurements: data.measurements,
        observations: data.observations,
        sharedAt: data.sharedAt?.toDate() || new Date(),
        sharedByName: data.sharedByName,
      });
    });

    return items;
  } catch (error) {
    console.error('Erro ao buscar itens compartilhados:', error);
    return [];
  }
};

/**
 * Busca itens que EU compartilhei com outros
 */
export const getItemsSharedByMe = async (userId: string): Promise<SharedInventoryItem[]> => {
  try {
    const q = query(
      collection(db, SHARED_INVENTORY_COLLECTION),
      where('originalOwnerId', '==', userId)
    );
    const snapshot = await getDocs(q);

    const items: SharedInventoryItem[] = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      items.push({
        id: docSnap.id,
        originalItemId: data.originalItemId,
        originalOwnerId: data.originalOwnerId,
        sharedWithUserId: data.sharedWithUserId,
        itemName: data.itemName,
        description: data.description,
        photoUrl: data.photoUrl,
        photoUrl2: data.photoUrl2,
        quantity: data.quantity,
        unitValue: data.unitValue,
        brand: data.brand,
        store: data.store,
        category: data.category,
        measurements: data.measurements,
        observations: data.observations,
        sharedAt: data.sharedAt?.toDate() || new Date(),
        sharedByName: data.sharedByName,
      });
    });

    return items;
  } catch (error) {
    console.error('Erro ao buscar itens compartilhados por mim:', error);
    return [];
  }
};

/**
 * Remove compartilhamento de um item
 */
export const removeSharedItem = async (shareId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, SHARED_INVENTORY_COLLECTION, shareId));
  } catch (error) {
    console.error('Erro ao remover compartilhamento:', error);
    throw error;
  }
};

/**
 * Copia item compartilhado para o inventário próprio
 */
export const copySharedItemToInventory = async (
  sharedItem: SharedInventoryItem,
  userId: string
): Promise<string> => {
  try {
    const newItemData = {
      itemName: sharedItem.itemName,
      description: sharedItem.description || null,
      photoUrl: sharedItem.photoUrl || null,
      photoUrl2: sharedItem.photoUrl2 || null,
      quantity: sharedItem.quantity || 1,
      unitValue: sharedItem.unitValue || null,
      brand: sharedItem.brand || null,
      store: sharedItem.store || null,
      category: sharedItem.category || null,
      measurements: sharedItem.measurements || null,
      observations: `Copiado de ${sharedItem.sharedByName}. ${sharedItem.observations || ''}`.trim(),
      userId,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const docRef = await addDoc(collection(db, 'inventoryItems'), newItemData);
    return docRef.id;
  } catch (error) {
    console.error('Erro ao copiar item para inventário:', error);
    throw error;
  }
};
